import { useCallback, useEffect, useRef } from 'react';
import { useOperatorTasks } from '@/entities/OperatorTask/model/store';
import { refreshAdminAccess } from '@/entities/AccessManagement/model/refreshAdminAccess';

/** Поддерживает актуальность общей очереди уведомлений */
export const useTaskNotificationPolling = (enabled: boolean) => {
    const refreshRevision = useOperatorTasks((state) => state.notificationRefreshRevision);
    const kickRef = useRef<(() => void) | null>(null);

    useEffect(() => {
        if (!enabled) return;

        let disposed = false;
        let active = false;
        let queued = false;
        let permissionChecked = false;
        let timer: ReturnType<typeof setTimeout> | undefined;
        let controller: AbortController | null = null;

        /** Обновляет данные общей смены */
        const refresh = async () => {
            if (disposed || document.hidden || !navigator.onLine) return;
            if (timer) clearTimeout(timer);
            if (active) {
                queued = true;

                return;
            }

            active = true;
            controller = new AbortController();
            const startedAt = Date.now();

            try {
                const store = useOperatorTasks.getState();
                await store.fetchSummary(true, true, controller.signal);

                if (disposed) return;

                if (useOperatorTasks.getState().summaryHttpStatus === 403 && !permissionChecked) {
                    permissionChecked = true;
                    await refreshAdminAccess();
                } else if (useOperatorTasks.getState().summaryHttpStatus !== 403) {
                    permissionChecked = false;
                }

                if (!disposed) await useOperatorTasks.getState().fetchOptions();

                if (!disposed && useOperatorTasks.getState().notificationsOpen) {
                    await useOperatorTasks.getState().fetchNotifications(controller.signal);
                }

            } finally {
                active = false;
                controller = null;

                if (!disposed) {
                    if (queued) {
                        queued = false;
                        void refresh();
                    } else {
                        const seconds = useOperatorTasks.getState().options?.pollIntervalSeconds ?? 30;
                        const delay = Math.max(0, Math.min(3600, Math.max(5, seconds)) * 1000 - (Date.now() - startedAt));
                        timer = setTimeout(() => void refresh(), delay);
                    }
                }
            }
        };

        /** Возобновляет обновление после возвращения к админке */
        const wake = () => { void refresh(); };

        kickRef.current = wake;
        wake();
        window.addEventListener('focus', wake);
        window.addEventListener('online', wake);
        document.addEventListener('visibilitychange', wake);

        return () => {
            disposed = true;
            kickRef.current = null;

            if (timer) clearTimeout(timer);
            controller?.abort();
            window.removeEventListener('focus', wake);
            window.removeEventListener('online', wake);
            document.removeEventListener('visibilitychange', wake);
        };
    }, [enabled]);

    useEffect(() => { if (refreshRevision > 0) kickRef.current?.(); }, [refreshRevision]);

    return useCallback(() => kickRef.current?.(), []);
};
